import { stripNonNumerics } from './util';
import upperFirst from 'lodash/upperFirst';
import { CountryCode } from '@/store/ui'


export function joinWithSuffix(items: string[], separator: string = ', ', suffix: string = ' & '): string {
  if (items.length == 0) {
    return '';
  }

  if (items.length == 1) {
    return items[0];
  }

  if (items.length == 2) {
    return `${items[0]}${suffix}${items[1]}`;
  }

  return `${items.slice(0, items.length - 1).join(`${separator}`)}${suffix}${items[items.length - 1]}`;
}

export function slugify(str: string): string {
  return str.replace(/[ ]/g, '-').toLowerCase();
}

export function camelCaseObject<T extends object>(obj: object): T {
  return caseObject<T>(obj, str => str[0].toLocaleLowerCase().concat(str.slice(1)));
}

export function pascalCaseObject<T extends object>(obj: object): T {
  return caseObject<T>(obj, str => str[0].toLocaleUpperCase().concat(str.slice(1)));
}

export function numberToWords(num: number): string {
  switch (num) {
    case 0:
      return 'zero';
    case 1:
      return 'one';
    case 2:
      return 'two';
    case 3:
      return 'three';
    case 4:
      return 'four';
    case 5:
      return 'five';
    case 6:
      return 'six';
    case 7:
      return 'seven';
    case 8:
      return 'eight';
    case 9:
      return 'nine';
    case 10:
      return 'ten';
    default:
      return 'more than ten';
  }
}

/** Formats phone number from 5551234567 -> (555) 123-4567 */
export function formatPhoneNumber(phoneNumber: string): string {
  phoneNumber = stripNonNumerics(phoneNumber);

  if (phoneNumber.length == 11) {
    return `+${phoneNumber[0]}-${phoneNumber.substr(1, 3)}-${phoneNumber.substr(4, 3)}-${phoneNumber.substr(7)}`;
  } else if (phoneNumber.length == 10) {
    return `(${phoneNumber.substr(0, 3)}) ${phoneNumber.substr(3, 3)}-${phoneNumber.substr(6)}`;
  }
  return phoneNumber;
}

//TODO Implement a function can interprets the mask value coming from the country in the constans.ts file to work more generically. 
export function formatPostalCode(code: string, countryCode: string = CountryCode.US): string {
  switch (countryCode) {
    case CountryCode.CA:
      return formatCanadaPostalCode(code);
    default:
      return code;
  }
}

export function formatCanadaPostalCode(code: string): string {
  let result = '';

  code = code.toUpperCase();

  // when autofilling from search
  if (code.length == 7) {
    result = code;
  } else if (code.length < 3) {
    result = code;
  } else if (code.length == 3) {
    result = code + ' ';
  } else if (code.length > 3) {
    result = `${code.substring(0, 3)} ${code.substring(3)}`;
  }

  return result;
}

/** Formats expiration date from 1123 -> 11/23 */
export function formatDate(date: string): string {
  let result = '';
  let lastCharIndex = 0;

  if (date.length > 2) {
    result += date.substring(0, 2) + '/';
    lastCharIndex = 2;
  }

  return result + date.substring(lastCharIndex);
}

export function formatGiftCardNumberWithSpaces(giftCardNumber: string): string {
  return [...giftCardNumber]
    .map((digit, i) => (i % 4 == 0 ? ` ${digit}` : digit))
    .join('')
    .trim();
}

function caseString(str: string | null, transformFn: (str: string) => string): string {
  if (!str) {
    return '';
  }

  return transformFn(str);
}

function caseObject<T extends object>(obj: object, transformFn: (str: string) => string): T {
  return Object.entries(obj).reduce((acc, entry) => {
    const key = caseString(entry[0], transformFn);
    const value = !!entry[1] && !Array.isArray(entry[1]) && typeof entry[1] === 'object' ? caseObject(entry[1], transformFn) : entry[1];

    acc[key] = value;

    return acc;
  }, {} as any);
}

// data & qa title => Data & Qa Title
export function capitalizeEveryWord(str?: string): string {
  if (!str) {
    return '';
  }

  return str
    .split(' ')
    .map(upperFirst)
    .join(' ');
}

export function addLeadingZero(num: number): string {
  if(num >= 0) {
    return num.toString().padStart(2, '0')
  }
  return '';
}

export function removeSpecialCharacters(str?: string): string | undefined {
  return str?.replace(/[^a-zA-Z0-9 -]/g, '');
}
